import { useState } from 'react'
import { Card, Title, Text, Flex, Badge, Button } from '@tremor/react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { PhoneIcon } from '@heroicons/react/24/outline'
import { telephonyTestsApi } from '@/api/telephonyTests'
import type { TelephonyTestSummary } from '@/api/telephonyTests'

const STATUS_CONFIG: Record<string, { color: string; label: string }> = {
  passed: { color: 'green', label: 'OK' },
  failed: { color: 'red', label: 'Echoue' },
  partial: { color: 'yellow', label: 'Partiel' },
  running: { color: 'blue', label: 'En cours...' },
  error: { color: 'red', label: 'Erreur' },
  skipped: { color: 'gray', label: 'Ignore' },
  timeout: { color: 'red', label: 'Timeout' },
}

const TEST_CATEGORIES = [
  { key: 'trunk', label: 'Trunks' },
  { key: 'sip', label: 'SIP' },
  { key: 'outbound', label: 'Appel sortant' },
  { key: 'inbound', label: 'Appel entrant' },
  { key: 'smartpush', label: 'SmartPush' },
] as const

function formatRelativeTime(dateStr: string | null): string {
  if (!dateStr) return '-'
  const date = new Date(dateStr)
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffMin = Math.floor(diffMs / 60000)

  if (diffMin < 1) return "A l'instant"
  if (diffMin < 60) return `Il y a ${diffMin} min`
  const diffH = Math.floor(diffMin / 60)
  if (diffH < 24) return `Il y a ${diffH}h`
  const diffD = Math.floor(diffH / 24)
  return `Il y a ${diffD}j`
}

function formatScheduledTime(dateStr: string | null): string {
  if (!dateStr) return 'Non programme'
  const date = new Date(dateStr)
  return date.toLocaleTimeString('fr-FR', {
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'Africa/Casablanca',
  })
}

export function TelephonyTestWidget() {
  const queryClient = useQueryClient()
  const [selectedCategories, setSelectedCategories] = useState<string[]>([])

  const { data: summary, isLoading } = useQuery<TelephonyTestSummary>({
    queryKey: ['telephony-test-summary'],
    queryFn: telephonyTestsApi.getSummary,
    refetchInterval: 30000,
  })

  const triggerMutation = useMutation({
    mutationFn: (categories?: string[]) =>
      telephonyTestsApi.triggerTest(categories && categories.length > 0 ? categories : undefined),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['telephony-test-summary'] })
    },
  })

  const disabled = summary?.enabled === false
  const lastRun = summary?.last_run
  const statusConfig = lastRun ? STATUS_CONFIG[lastRun.status] || STATUS_CONFIG.error : null
  const isRunning = summary?.is_running || triggerMutation.isPending

  const toggleCategory = (key: string) => {
    setSelectedCategories((prev) =>
      prev.includes(key) ? prev.filter((c) => c !== key) : [...prev, key]
    )
  }

  return (
    <Card>
      <Flex justifyContent="between" alignItems="center" className="mb-4">
        <Flex justifyContent="start" className="gap-2" alignItems="center">
          <PhoneIcon className="w-5 h-5 text-gray-500" />
          <Title className="text-lg">Test Téléphonie</Title>
        </Flex>
        {disabled ? (
          <Badge color="gray">Désactivé</Badge>
        ) : statusConfig ? (
          <Badge color={statusConfig.color as any}>{statusConfig.label}</Badge>
        ) : null}
      </Flex>

      {isLoading ? (
        <Text className="text-gray-500">Chargement...</Text>
      ) : disabled ? (
        <Text className="text-gray-500">
          Tests de téléphonie désactivés. À réactiver ultérieurement.
        </Text>
      ) : !lastRun ? (
        <Text className="text-gray-500">Aucun test exécuté</Text>
      ) : (
        <div className="space-y-3">
          {/* Last run info */}
          <div className="space-y-1">
            <Flex justifyContent="between">
              <Text className="text-sm text-gray-500">Dernier test</Text>
              <Text className="text-sm font-medium">
                {formatRelativeTime(lastRun.started_at)}
              </Text>
            </Flex>
            <Flex justifyContent="between">
              <Text className="text-sm text-gray-500">Resultat</Text>
              <Text className="text-sm font-medium">
                {lastRun.passed_checks}/{lastRun.total_checks} OK
                {lastRun.failed_checks > 0 && (
                  <span className="text-red-500 ml-1">
                    ({lastRun.failed_checks} echoue{lastRun.failed_checks > 1 ? 's' : ''})
                  </span>
                )}
              </Text>
            </Flex>
            {lastRun.duration_seconds != null && (
              <Flex justifyContent="between">
                <Text className="text-sm text-gray-500">Duree</Text>
                <Text className="text-sm font-medium">
                  {Math.round(lastRun.duration_seconds)}s
                </Text>
              </Flex>
            )}
          </div>

          {/* Individual test results */}
          {summary?.last_run_results && summary.last_run_results.length > 0 && (
            <div className="space-y-1 pt-2 border-t border-gray-100 dark:border-gray-800">
              {summary.last_run_results.map((result) => {
                const rConfig = STATUS_CONFIG[result.status] || STATUS_CONFIG.error
                return (
                  <Flex key={result.id} justifyContent="between" alignItems="center">
                    <Text className="text-xs text-gray-600 dark:text-gray-400 truncate max-w-[160px]">
                      {result.test_name.replace(/_/g, ' ')}
                    </Text>
                    <Badge size="xs" color={rConfig.color as any}>
                      {rConfig.label}
                    </Badge>
                  </Flex>
                )
              })}
            </div>
          )}

          {/* Next scheduled */}
          {summary?.next_scheduled && (
            <Flex justifyContent="between" className="pt-2 border-t border-gray-100 dark:border-gray-800">
              <Text className="text-sm text-gray-500">Prochain test</Text>
              <Text className="text-sm font-medium">
                {formatScheduledTime(summary.next_scheduled)}
              </Text>
            </Flex>
          )}
        </div>
      )}

      {/* Manual test controls */}
      {!disabled && (
      <div className="mt-4 space-y-3 pt-3 border-t border-gray-100 dark:border-gray-800">
        <Text className="text-xs text-gray-500 font-medium">Test manuel</Text>

        {/* Category selector chips */}
        <div className="flex flex-wrap gap-1.5">
          {TEST_CATEGORIES.map(({ key, label }) => (
            <button
              key={key}
              type="button"
              disabled={isRunning}
              onClick={() => toggleCategory(key)}
              className={`px-2 py-0.5 rounded-full text-xs font-medium transition-colors ${
                selectedCategories.includes(key)
                  ? 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300'
                  : 'bg-gray-100 text-gray-500 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-400 dark:hover:bg-gray-700'
              } ${isRunning ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
            >
              {label}
            </button>
          ))}
        </div>

        {/* Run button */}
        <Button
          size="xs"
          variant="secondary"
          loading={isRunning}
          disabled={isRunning}
          onClick={() => triggerMutation.mutate(selectedCategories)}
          className="w-full"
        >
          {isRunning
            ? 'Test en cours...'
            : selectedCategories.length > 0
              ? `Tester (${selectedCategories.length})`
              : 'Tout tester'}
        </Button>
        {triggerMutation.isError && (
          <Text className="text-xs text-red-500">
            {(triggerMutation.error as any)?.response?.data?.detail || 'Erreur lors du lancement'}
          </Text>
        )}
      </div>
      )}
    </Card>
  )
}
